"use client";

/**
 * ROOT 404 - catches every unmatched URL (including /az/*, since
 * [locale] has no catch-all route), so this is the one people see.
 * It sits OUTSIDE the locale provider, so the language comes from the
 * URL prefix instead (falls back to Azerbaijani, the audience), and the
 * copy is the same dictionary the [locale] variant uses. Same anatomy
 * as that variant: quiet status code, plain heading, one cause
 * sentence, one indigo action, history as the bordered secondary. No
 * illustration, no bilingual subline (both read as a different, worse
 * page next to the locale version).
 */
import Link from "@/lib/nav-link";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { NotFoundScene } from "@/components/animations/SceneAnimations";

// The translation registry is filled by the [locale] layout / provider,
// which this root route sits outside of - so the four strings live here
// (kept in sync with notfound.* in lib/i18n/{az,ru,en}.ts).
const COPY = {
  az: { title: "Bu səhifə mövcud deyil", body: "Link köhnələ bilər və ya elan artıq götürülüb. Analiz tarixçəniz profilinizdə qalır.", home: "Ana səhifəyə qayıdın" },
  en: { title: "This page doesn't exist", body: "The link may be stale, or the listing has been taken down. Your analysis history is safe in your profile.", home: "Back to home" },
  ru: { title: "Такой страницы нет", body: "Ссылка могла устареть, или объявление уже снято. История анализов сохранена в вашем профиле.", home: "На главную" },
} as const;
type Locale = keyof typeof COPY;

function localeFromPath(path: string | null): Locale {
  const seg = (path ?? "").split("/")[1];
  return seg === "ru" || seg === "en" ? seg : "az";
}

export default function NotFound() {
  const locale = localeFromPath(usePathname());
  const c = COPY[locale];
  // A client page cannot export metadata: the tab title is set here.
  useEffect(() => { document.title = `${c.title} | Mənzil.ai`; }, [c.title]);
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-2xl rounded-2xl border border-gray-200 bg-white p-8 sm:p-12">
        {/* Illustration first on phones, beside the text from sm up: a
            person scanning the skyline with binoculars - the page is
            being looked for, not missing. Large on purpose (owner). */}
        <div className="flex flex-col-reverse gap-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-gray-400 tabular-nums">404</div>
            <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight leading-[1.15] text-gray-900">
              {c.title}
            </h1>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-gray-600">{c.body}</p>
            <div className="mt-6">
              <Link
                href={`/${locale}`}
                className="inline-flex rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
              >
                {c.home}
              </Link>
            </div>
          </div>
          <div className="h-52 w-64 shrink-0 self-center sm:h-60 sm:w-[300px]">
            <NotFoundScene />
          </div>
        </div>
      </div>
    </div>
  );
}
